import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { StoryblokImage } from "@wojo/storyblok";
import { MediaSlider, Modal, VideoPlayer } from "@wojo/ui";
import { useTourPageContext } from "../../_client/context";
import styles from "./TourMediaSliderModal.module.scss";

type TourMediaSliderModalProps = {
    startIndex?: number;
    open?: boolean;
    onCloseRequested: () => void;
};

export const TourMediaSliderModal: React.FC<TourMediaSliderModalProps> = ({
    open = false,
    startIndex = 0,
    onCloseRequested,
}) => {
    const { tourContent } = useTourPageContext();
    const { productPage } = useI18n("productPage");

    const slides = tourContent.assets
        .map((asset, index) => {
            if (asset.__typename === "TourImage") {
                return {
                    caption: asset.caption ?? "",
                    media: (
                        <StoryblokImage
                            imageClassName={styles["image-container"]}
                            mobile={{
                                size: {
                                    height: 700,
                                },
                                asset: {
                                    filename: asset.image?.filename,
                                    title: asset.image?.title,
                                    alt: asset.image?.alt,
                                    id: asset.image?.id,
                                    name: asset.image?.name,
                                },
                            }}
                            desktop={{
                                size: {
                                    height: 1000,
                                },
                                asset: {
                                    filename: asset.image?.filename,
                                    title: asset.image?.title,
                                    alt: asset.image?.alt,
                                    id: asset.image?.id,
                                    name: asset.image?.name,
                                },
                            }}
                            key={(asset.image?.filename ?? "") + index}
                        />
                    ),
                    thumbnail: (
                        <StoryblokImage
                            imageClassName={styles["thumbnail-container"]}
                            mobile={{
                                size: {
                                    height: 160,
                                },
                                asset: {
                                    filename: asset.image?.filename,
                                    title: asset.image?.title,
                                    alt: asset.image?.alt,
                                    id: asset.image?.id,
                                    name: asset.image?.name,
                                },
                            }}
                            key={(asset.image?.filename ?? "") + index}
                        />
                    ),
                };
            } else if (asset.__typename === "TourVideo") {
                return {
                    caption: asset.caption ?? "",
                    media: (
                        <div
                            className={styles["video-container"]}
                            key={asset.wistiaVideoId}
                        >
                            <VideoPlayer
                                videoId={asset.wistiaVideoId ?? ""}
                                requestAutoplay={false}
                                loopVideo={true}
                                playerColor="#191919"
                                style={{
                                    height: "100%",
                                }}
                                onPlaybackChange={(videoState) => {
                                    gtmSendEvent({
                                        event: "track",
                                        ga4_object: "TOUR_MEDIA_SLIDER_VIDEO",
                                        ga4_action: videoState.toUpperCase(),
                                        videoId: asset.wistiaVideoId,
                                    });
                                }}
                            />
                        </div>
                    ),
                    thumbnail: (
                        <div
                            className={styles["video-container"]}
                            key={asset.wistiaVideoId}
                        >
                            <VideoPlayer
                                videoId={asset.wistiaVideoId ?? ""}
                                requestAutoplay={false}
                                loopVideo={true}
                                playerColor="#191919"
                                style={{
                                    height: "100%",
                                }}
                                onPlaybackChange={(videoState) => {
                                    gtmSendEvent({
                                        event: "track",
                                        ga4_object:
                                            "TOUR_MEDIA_SLIDER_VIDEO_THUMBNAIL",
                                        ga4_action: videoState.toUpperCase(),
                                        videoId: asset.wistiaVideoId,
                                    });
                                }}
                            />
                        </div>
                    ),
                };
            }
        })
        .filter(
            (
                slide,
            ): slide is {
                caption: string;
                media: JSX.Element;
                thumbnail: JSX.Element;
            } => !!slide,
        );

    return (
        <Modal
            isOpen={open}
            onCloseRequested={onCloseRequested}
            accessibleTitle={productPage.tourHero.titles.mediaSlider.l()}
            variant="fullscreen"
        >
            <MediaSlider
                startIndex={startIndex}
                slides={slides}
                onSliderButtonClick={(direction: "next" | "prev") => {
                    const directionText =
                        direction === "prev" ? "back arrow" : "forward arrow";
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_action: "SCROLLED",
                            ga4_object: "PHOTO_GALLERY",
                            galleryType: "itineraryGallery",
                            mediaType: "PHOTO",
                            eventCategory: "product-page",
                            eventAction: "photo gallery",
                            eventLabel: directionText,
                        },
                    });
                }}
            />
        </Modal>
    );
};
